import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { apiFetch, downloadBlob } from "../../../api/fddClient";
import {
  buildDisplayRows, commitDraft, coverageSummary, draftFrom, draftMissing, fileLabel, syncDraft,
  type PeriodDraft, type PeriodKind,
} from "./monthRows";
import { parseSheetPeriod, planWorkbook, type FileLayout } from "./sheetPeriods";

/**
 * TB Aggregator — combine many monthly trial balance files into one wide
 * monthly TB, with no AI in the numbers path.
 *
 * Three screens:
 *   setup    — entity name, drop files, confirm the month read from each filename
 *   tagging  — pick a template file, click its header row, tag columns, rules
 *   review   — per-file match status + checks, inspect/re-tag any file, build
 *
 * The client owns all configuration; the server only holds parsed cells.
 * Types below mirror server/tb-aggregator/types.ts.
 */

type Period = { year: number; month: number };
type TagRole =
  | "entity" | "account_number" | "account_name" | "split"
  | "group1" | "group2" | "group3"
  | "amount" | "debit" | "credit" | "period_amount" | "entity_amount";
type ColumnTag = { role: TagRole; col: number; label: string; period?: Period; display?: string };
type SplitKind = "IS" | "BS" | "NA";
type SplitValue = { value: string; rows: number; guess: "IS" | "BS" | null };
type ColumnGranularity = "monthly" | "fiscal";
type FiscalSettings = { fyEndMonth: number; ytdThrough: Period | null };
type NetFormula = "amount" | "debit_minus_credit" | "debit_plus_credit";
type Rules = {
  excludeBlankAccountNumber: boolean;
  excludeTotalRows: boolean;
  excludeZeroRows: boolean;
  customExcludeText: string;
  netFormula: NetFormula;
};
type EntityMode = "single" | "spread";

type ShiftSuggestion = { offset: number; score: number; of: number };
type SheetInfo = { name: string; rows: number; cols: number };

/** An uploaded workbook. Its sheets become the units below. */
type FileMeta = {
  fileId: string;
  name: string;
  sheets: SheetInfo[];
  /** Fiscal year read from the filename, used to place sheets named "P1". */
  yearHint: number | null;
};

/**
 * One worksheet of one file: the unit of work everywhere downstream.
 *
 * A workbook holding a sheet per month produces one unit per sheet, so a
 * year in one file and a month in one file are the same thing to the rest of
 * the tool. Sheets that are not trial balances arrive with include false.
 */
type SheetUnit = {
  id: string;
  fileId: string;
  fileName: string;
  sheetName: string;
  rows: number;
  include: boolean;
  entity: string;
  period: Period | null;
  periodKind: PeriodKind;
  override: { headerRow: number; tags: ColumnTag[] } | null;
  shift: number | null;
};
type Preview = {
  sheetName: string;
  rows: string[][];
  totalRows: number;
  totalCols: number;
  headerRowGuess: number;
  periodByCell: Record<string, Period>;
};
type FileStatus = "matched" | "partial" | "unmatched" | "manual" | "shifted" | "error";
type FileAnalysis = {
  /** Identifies the (file, sheet) this analysis came from. */
  id: string;
  fileId: string;
  fileName: string;
  entity: string;
  period: Period | null;
  sheetName: string;
  headerRow: number;
  tags: ColumnTag[];
  missingRoles: TagRole[];
  status: FileStatus;
  error?: string;
  rowsTotal: number;
  rowsIncluded: number;
  excluded: Record<string, number>;
  net: number;
  netOk: boolean;
  /** Each entity/period that does not net to zero, worst first. */
  netBreaks: { entity: string; period: string; net: number }[];
  /** Amount cells that held text, left out of the sums. */
  textAmountCells: number;
  duplicateAccounts: string[];
  periods: string[];
  entitiesFound: string[];
  missingEntities: string[];
  periodKind: PeriodKind;
  suggestedShift?: ShiftSuggestion | null;
  appliedShift?: number | null;
};
type CrossChecks = {
  missingPeriods: { entity: string; periods: string[] }[];
  duplicatePeriods: { entity: string; period: string; files: string[] }[];
  partialCoverage: { entity: string; account: string; name: string; presentIn: number; of: number }[];
  partialCoverageTotal: number;
  identicalColumns: { labels: string[] }[];
};
type AnalyzeResponse = {
  files: FileAnalysis[];
  cross: CrossChecks;
  periods: string[];
  columnLabels: string[];
  rowCount: number;
  splitSource: "split_column" | "mapping1" | "none";
  splitValues: SplitValue[];
  splitNeedsInput: boolean;
  splitExcludedRows: number;
  unclassifiedRows: number;
};

type Phase = "setup" | "tagging" | "review";

const ACCEPT = ".xlsx,.xls,.csv";
const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
const THIS_YEAR = new Date().getFullYear();
const YEARS: number[] = [];
for (let y = THIS_YEAR + 1; y >= 2015; y--) YEARS.push(y);

const ROLE_OPTIONS: { value: TagRole | "ignore"; label: string }[] = [
  { value: "ignore", label: "(ignore)" },
  { value: "account_number", label: "Account #" },
  { value: "account_name", label: "Account Name" },
  { value: "amount", label: "Amount (net)" },
  { value: "debit", label: "Debit" },
  { value: "credit", label: "Credit" },
  { value: "period_amount", label: "Period column" },
  { value: "entity_amount", label: "Entity column" },
  { value: "entity", label: "Entity" },
  { value: "split", label: "IS / BS Split" },
  { value: "group1", label: "Mapping 1" },
  { value: "group2", label: "Mapping 2" },
  { value: "group3", label: "Mapping 3" },
];
const ROLE_LABEL: Record<TagRole, string> = Object.fromEntries(
  ROLE_OPTIONS.filter((o) => o.value !== "ignore").map((o) => [o.value, o.label]),
) as Record<TagRole, string>;

const DEFAULT_RULES: Rules = {
  excludeBlankAccountNumber: true,
  excludeTotalRows: true,
  excludeZeroRows: false,
  customExcludeText: "",
  netFormula: "amount",
};

// ── helpers ──

const normalizeLabel = (s: string) => s.toLowerCase().replace(/\s+/g, " ").trim();
/** Mirrors splitKey on the server, so the map the analyst builds lines up. */
const splitKeyOf = (s: string) => String(s ?? "").toLowerCase().replace(/&/g, " and ").replace(/[^a-z0-9]+/g, " ").trim();
const periodLabel = (p: Period) => `${MONTHS[p.month - 1]}-${String(p.year).slice(2)}`;
const keyLabel = (k: string) => {
  const [y, m] = k.split("-").map((x) => parseInt(x, 10));
  return periodLabel({ year: y, month: m });
};
const fmtNum = (n: number) => n.toLocaleString("en-US", { maximumFractionDigits: 0 });

/** Shape the upload endpoint returns for each workbook. */
type UploadedFileDto = {
  fileId: string;
  name: string;
  sheets: SheetInfo[];
  derivedPeriod: Period | null;
  derivedYear: number | null;
  entity: string;
  /** Sheets the server left out as too large, one line each. */
  skippedSheets?: string[];
};
type UploadResponse = {
  sessionId: string;
  files: UploadedFileDto[];
  rejected?: { name: string; reason: string }[];
};

export const unitId = (fileId: string, sheetName: string) => `${fileId}|${sheetName}`;

/**
 * Turn one workbook into the sheets it contributes.
 *
 * A workbook is one period unless several of its sheets are named like
 * periods — "P1".."P12", "Jan".."Dec" — in which case it is a year of
 * monthly trial balances and each sheet becomes its own row. Everything else
 * stays a single row, so a file with thirty unrelated tabs does not flood
 * the list; its sheet chooser is there if the analyst wants another one.
 */
function planUnits(
  meta: FileMeta,
  filePeriod: Period | null,
  entity: string,
  fyEndMonth: number,
  layout: FileLayout,
): SheetUnit[] {
  const plan = planWorkbook(meta.sheets.map((s) => s.name), layout, meta.yearHint, fyEndMonth, filePeriod);
  return plan.map((x) => ({
    id: unitId(meta.fileId, x.sheetName),
    fileId: meta.fileId,
    fileName: meta.name,
    sheetName: x.sheetName,
    rows: meta.sheets.find((s) => s.name === x.sheetName)?.rows ?? 0,
    include: x.include,
    entity,
    period: x.period,
    periodKind: layout === "fy" ? "fy" : "month",
    override: null,
    shift: null,
  }));
}

/** Deterministic first-pass tag guess from header text. The user confirms every one. */
function guessTags(headerCells: string[], headerRow: number, periodByCell: Record<string, Period>): ColumnTag[] {
  const tags: ColumnTag[] = [];
  const taken = new Set<TagRole>();
  const add = (role: TagRole, col: number, period?: Period) => {
    if (role !== "period_amount" && taken.has(role)) return;
    taken.add(role);
    tags.push({ role, col, label: normalizeLabel(headerCells[col] ?? ""), period });
  };
  headerCells.forEach((raw, col) => {
    const h = normalizeLabel(raw);
    const p = periodByCell[`${headerRow}:${col}`];
    if (p) { add("period_amount", col, p); return; }
    if (!h) return;
    if (/^(gl\s*)?(acct|account)?\s*(#|no\.?|num|number|code|id)$/.test(h) || /^(gl|account|acct)\s*(#|no\.?|number|code)/.test(h) || h === "gl" || h === "acct" || h === "account") add("account_number", col);
    else if (/(account|acct|gl)\s*(name|description|desc|title)|^description$|^name$|^account name$/.test(h)) add("account_name", col);
    else if (/^debits?$|^dr$/.test(h)) add("debit", col);
    else if (/^credits?$|^cr$/.test(h)) add("credit", col);
    else if (/^(net|balance|amount|ending balance|end balance|net balance|closing balance|ytd|total)$/.test(h)) add("amount", col);
    else if (/^(entity|company|legal entity|subsidiary)$/.test(h)) add("entity", col);
  });
  return tags;
}

function templateProblem(tags: ColumnTag[], entityMode: EntityMode): string | null {
  const roles = new Set(tags.map((t) => t.role));
  if (!roles.has("account_number") && !roles.has("account_name")) return "Tag an Account # or Account Name column.";
  if (entityMode === "spread" && !roles.has("entity_amount")) {
    return "Mark the first and last entity column with Tag a range.";
  }
  const hasValue = roles.has("amount") || (roles.has("debit") && roles.has("credit"))
    || roles.has("period_amount") || roles.has("entity_amount");
  if (!hasValue) return "Tag an Amount column, a Debit and Credit pair, or one or more Period columns.";
  if (roles.has("debit") !== roles.has("credit") && !roles.has("amount") && !roles.has("period_amount") && !roles.has("entity_amount")) {
    return "Debit and Credit go together — tag both.";
  }
  for (const t of tags) if (t.role === "period_amount" && !t.period) return "Every Period column needs a month and year.";
  return null;
}

const shiftText = (offset: number) => `${offset > 0 ? "right" : "left"} by ${Math.abs(offset)} column${Math.abs(offset) === 1 ? "" : "s"}`;

async function postJson<T>(path: string, body: unknown): Promise<T> {
  const res = await apiFetch(path, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(body) });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err?.error || `Request failed (HTTP ${res.status})`);
  }
  return res.json() as Promise<T>;
}

/** Files sent per upload request. Keeps each request small and shows progress. */
const UPLOAD_BATCH = 10;

// ── styles ──

const GREEN = "var(--theme-ink)";
const card: React.CSSProperties = { background: "#ffffff", border: "1px solid var(--theme-border)", borderRadius: 8, padding: 12, display: "flex", flexDirection: "column", gap: 10 };
const h2: React.CSSProperties = { fontSize: 12, fontWeight: 700, color: GREEN };
const hint: React.CSSProperties = { fontSize: 11, color: "#6b7280" };
const selectStyle: React.CSSProperties = { fontSize: 11, padding: "2px 4px", borderRadius: 4, border: "1px solid var(--theme-border)", background: "#fff" };
const inputStyle: React.CSSProperties = { fontSize: 12, padding: "4px 6px", borderRadius: 4, border: "1px solid var(--theme-border)" };
const th: React.CSSProperties = { borderBottom: "1px solid var(--theme-border)", padding: "4px 6px", fontWeight: 700, color: GREEN, textAlign: "left", background: "var(--theme-tint-7)", fontSize: 11, whiteSpace: "nowrap" };
const td: React.CSSProperties = { borderBottom: "1px solid #f1f5f9", padding: "3px 6px", color: "#374151", fontSize: 11, whiteSpace: "nowrap", verticalAlign: "top" };
const primaryBtn = (disabled: boolean): React.CSSProperties => ({
  padding: "8px 12px", borderRadius: 8, border: "none", background: disabled ? "#9ca3af" : "var(--theme-accent)",
  color: "#fff", fontWeight: 700, fontSize: 13, cursor: disabled ? "not-allowed" : "pointer",
});
const ghostBtn: React.CSSProperties = { fontSize: 11, padding: "3px 8px", border: "1px solid var(--theme-border)", background: "var(--theme-tint-7)", borderRadius: 4, cursor: "pointer", color: GREEN, fontWeight: 600 };
const dangerBtn: React.CSSProperties = { background: "transparent", color: "#dc2626", border: "1px solid #fca5a5", borderRadius: 6, padding: "2px 8px", cursor: "pointer", fontSize: 11, fontWeight: 600 };

const STATUS_STYLE: Record<FileStatus, { bg: string; fg: string; text: string }> = {
  matched: { bg: "#dcfce7", fg: "var(--theme-action)", text: "Matched" },
  shifted: { bg: "#dcfce7", fg: "var(--theme-action)", text: "Shifted" },
  manual: { bg: "#dbeafe", fg: "#1d4ed8", text: "Manual" },
  partial: { bg: "#fef3c7", fg: "#b45309", text: "Partial" },
  unmatched: { bg: "#fee2e2", fg: "#b91c1c", text: "Unmatched" },
  error: { bg: "#fee2e2", fg: "#b91c1c", text: "Error" },
};

// ── small components ──

/**
 * Month + year dropdowns. The half-entered state lives here: picking a month
 * before a year (or the other way round) has to stick, or neither dropdown
 * can ever be filled. The period is reported upward only once both are set.
 */
const PeriodPicker: React.FC<{ value: Period | null; onChange: (p: Period | null) => void; compact?: boolean }> = ({ value, onChange, compact }) => {
  const [draft, setDraft] = useState<PeriodDraft>(() => draftFrom(value));

  // Adopt a period that arrived from outside (filename derivation, a shift
  // being applied). A null value is "nothing committed", which must not
  // clobber what the analyst has half-entered.
  useEffect(() => {
    setDraft((prev) => syncDraft(prev, value));
  }, [value]);

  const commit = (next: PeriodDraft) => {
    setDraft(next);
    onChange(commitDraft(next));
  };
  const missing = draftMissing(draft);

  return (
    <span style={{ display: "inline-flex", gap: 4, alignItems: "center" }}>
      <select
        value={draft.month ?? ""}
        onChange={(e) => commit({ ...draft, month: e.target.value ? parseInt(e.target.value, 10) : null })}
        style={{ ...selectStyle, width: compact ? 58 : 66, borderColor: missing === "month" ? "#f59e0b" : "var(--theme-border)" }}
      >
        <option value="">—</option>
        {MONTHS.map((m, i) => <option key={m} value={i + 1}>{m}</option>)}
      </select>
      <select
        value={draft.year ?? ""}
        onChange={(e) => commit({ ...draft, year: e.target.value ? parseInt(e.target.value, 10) : null })}
        style={{ ...selectStyle, width: compact ? 62 : 70, borderColor: missing === "year" ? "#f59e0b" : "var(--theme-border)" }}
      >
        <option value="">—</option>
        {YEARS.map((y) => <option key={y} value={y}>{y}</option>)}
      </select>
      {missing && <span style={{ fontSize: 10, color: "#b45309", fontWeight: 600 }}>pick a {missing}</span>}
    </span>
  );
};

type SheetTaggerProps = {
  preview: Preview;
  headerRow: number;
  tags: ColumnTag[];
  onChange: (headerRow: number, tags: ColumnTag[]) => void;
};

/** One table: role dropdowns above each column, click a row number to make it the header row. */
const SheetTagger: React.FC<SheetTaggerProps> = ({ preview, headerRow, tags, onChange }) => {
  const cols = preview.rows.reduce((m, r) => Math.max(m, r.length), 0);
  const headerCells = preview.rows[headerRow] ?? [];
  const tagAt = (c: number) => tags.find((t) => t.col === c);

  // Range tagging. A consolidating TB can carry thirty entity columns, and
  // setting thirty dropdowns by hand is the kind of task people abandon.
  const [rangeRole, setRangeRole] = useState<"entity_amount" | "period_amount">("entity_amount");
  const [rangeFrom, setRangeFrom] = useState<number | null>(null);
  const [rangeTo, setRangeTo] = useState<number | null>(null);

  const applyRange = () => {
    if (rangeFrom == null || rangeTo == null) return;
    const lo = Math.min(rangeFrom, rangeTo);
    const hi = Math.max(rangeFrom, rangeTo);
    const kept = tags.filter((t) => t.col < lo || t.col > hi);
    const added: ColumnTag[] = [];
    for (let c = lo; c <= hi; c++) {
      const raw = (headerCells[c] ?? "").trim();
      if (rangeRole === "entity_amount") {
        // A blank header cannot name an entity, so it is left alone.
        if (!raw) continue;
        added.push({ role: "entity_amount", col: c, label: normalizeLabel(raw), display: raw });
      } else {
        const p = preview.periodByCell[`${headerRow}:${c}`];
        if (!p) continue;
        added.push({ role: "period_amount", col: c, label: normalizeLabel(raw), period: p });
      }
    }
    onChange(headerRow, [...kept, ...added].sort((a, b) => a.col - b.col));
  };

  const rangeCount = rangeFrom != null && rangeTo != null ? Math.abs(rangeTo - rangeFrom) + 1 : 0;
  const colChoices = Array.from({ length: cols }, (_, c) => ({
    c, text: (headerCells[c] ?? "").trim() || `Column ${c + 1}`,
  }));

  const setRole = (col: number, role: TagRole | "ignore") => {
    let next = tags.filter((t) => t.col !== col);
    if (role !== "ignore") {
      // Period and entity columns come in sets; every other role is unique.
      if (role !== "period_amount" && role !== "entity_amount") next = next.filter((t) => t.role !== role);
      const raw = (headerCells[col] ?? "").trim();
      next.push({
        role, col,
        label: normalizeLabel(raw),
        period: role === "period_amount" ? preview.periodByCell[`${headerRow}:${col}`] : undefined,
        display: role === "entity_amount" ? raw : undefined,
      });
    }
    next.sort((a, b) => a.col - b.col);
    onChange(headerRow, next);
  };

  const setPeriod = (col: number, p: Period | null) => {
    onChange(headerRow, tags.map((t) => (t.col === col ? { ...t, period: p ?? undefined } : t)));
  };

  const pickHeader = (r: number) => {
    if (r === headerRow) return;
    const cells = preview.rows[r] ?? [];
    const next = tags.map((t) => ({
      ...t,
      label: normalizeLabel(cells[t.col] ?? ""),
      period: t.role === "period_amount" ? preview.periodByCell[`${r}:${t.col}`] ?? t.period : undefined,
    }));
    onChange(r, next);
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap", padding: "6px 10px", background: "var(--theme-tint-7)", border: "1px solid var(--theme-tint-22)", borderRadius: 6 }}>
        <span style={{ fontSize: 11, fontWeight: 700, color: GREEN }}>Tag a range</span>
        <span style={hint}>from</span>
        <select value={rangeFrom ?? ""} onChange={(e) => setRangeFrom(e.target.value === "" ? null : parseInt(e.target.value, 10))} style={{ ...selectStyle, maxWidth: 190 }}>
          <option value="">first column…</option>
          {colChoices.map((o) => <option key={o.c} value={o.c}>{o.c + 1}. {o.text}</option>)}
        </select>
        <span style={hint}>through</span>
        <select value={rangeTo ?? ""} onChange={(e) => setRangeTo(e.target.value === "" ? null : parseInt(e.target.value, 10))} style={{ ...selectStyle, maxWidth: 190 }}>
          <option value="">last column…</option>
          {colChoices.map((o) => <option key={o.c} value={o.c}>{o.c + 1}. {o.text}</option>)}
        </select>
        <span style={hint}>as</span>
        <select value={rangeRole} onChange={(e) => setRangeRole(e.target.value as "entity_amount" | "period_amount")} style={selectStyle}>
          <option value="entity_amount">Entity columns</option>
          <option value="period_amount">Period columns</option>
        </select>
        <button type="button" onClick={applyRange} disabled={rangeFrom == null || rangeTo == null} style={{ ...ghostBtn, opacity: rangeFrom == null || rangeTo == null ? 0.5 : 1 }}>
          Tag {rangeCount || ""} column{rangeCount === 1 ? "" : "s"}
        </button>
        <span style={hint}>
          {rangeRole === "entity_amount"
            ? "Each column's heading becomes the entity name. Pick the first and last instead of setting each one."
            : "Each column's heading is read as a month."}
        </span>
      </div>
      <div style={{ overflowX: "auto", border: "1px solid #e5e7eb", borderRadius: 6, maxHeight: 460, overflowY: "auto" }}>
      <table style={{ borderCollapse: "collapse", width: "max-content" }}>
        <thead style={{ position: "sticky", top: 0, zIndex: 1 }}>
          <tr>
            <th style={{ ...th, background: "#f9fafb", color: "#9ca3af" }}>#</th>
            {Array.from({ length: cols }, (_, c) => {
              const tag = tagAt(c);
              return (
                <th key={c} style={{ ...th, background: "#f9fafb", minWidth: 120, padding: 4 }}>
                  <select value={tag?.role ?? "ignore"} onChange={(e) => setRole(c, e.target.value as TagRole | "ignore")} style={{ ...selectStyle, width: "100%", fontWeight: tag ? 700 : 400, color: tag ? GREEN : "#6b7280" }}>
                    {ROLE_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                  </select>
                  {tag?.role === "period_amount" && (
                    <div style={{ marginTop: 3 }}>
                      <PeriodPicker compact value={tag.period ?? null} onChange={(p) => setPeriod(c, p)} />
                    </div>
                  )}
                </th>
              );
            })}
          </tr>
          <tr>
            <th style={{ ...th, background: "#fff3a0", color: "#7c5e10" }}>{headerRow + 1}</th>
            {Array.from({ length: cols }, (_, c) => (
              <th key={c} style={{ ...th, background: "#fff3a0", color: "#7c5e10" }}>{headerCells[c] || <span style={{ color: "#c4a94a", fontWeight: 400 }}>Col {c + 1}</span>}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {preview.rows.map((row, r) => {
            const isHeader = r === headerRow;
            const above = r < headerRow;
            return (
              <tr key={r} style={{ background: isHeader ? "#fff3a0" : above ? "#fafafa" : r % 2 ? "#fcfcfc" : "#fff", opacity: above ? 0.55 : 1 }}>
                <td
                  onClick={() => pickHeader(r)}
                  title={isHeader ? "Current header row" : `Use row ${r + 1} as the header row`}
                  style={{ ...td, cursor: "pointer", color: "#7c5e10", fontWeight: 700, position: "sticky", left: 0, background: isHeader ? "#fff3a0" : "#fffdf4", borderRight: "1px solid #f1e6b3" }}
                >
                  {r + 1}
                </td>
                {Array.from({ length: cols }, (_, c) => (
                  <td key={c} style={{ ...td, maxWidth: 220, overflow: "hidden", textOverflow: "ellipsis", fontWeight: isHeader ? 700 : 400 }}>{row[c] ?? ""}</td>
                ))}
              </tr>
            );
          })}
        </tbody>
      </table>
      <div style={{ ...hint, padding: "4px 8px", fontSize: 10, color: "#9ca3af" }}>
        Showing first {preview.rows.length} of {preview.totalRows} rows · {cols} of {preview.totalCols} columns. Click a row number to make it the header row.
      </div>
      </div>
    </div>
  );
};

// ── main panel ──

type Props = { projectId: string; projectName?: string };

const TbAggregatorPanel: React.FC<Props> = ({ projectId, projectName }) => {
  const [phase, setPhase] = useState<Phase>("setup");
  const [sessionId, setSessionId] = useState<string | null>(null);
  // The session in a ref as well, for the unmount clean-up below.
  const sessionRef = useRef<string | null>(null);
  useEffect(() => { sessionRef.current = sessionId; }, [sessionId]);
  // Leaving the tool drops the uploads with it, so the server's copy of this
  // engagement's TBs goes too rather than waiting out its idle hour.
  useEffect(() => () => {
    const sid = sessionRef.current;
    if (sid) void postJson("/api/tb-aggregator/discard", { projectId, sessionId: sid }).catch(() => undefined);
  }, [projectId]);
  const [fileMetas, setFileMetas] = useState<FileMeta[]>([]);
  const [units, setUnits] = useState<SheetUnit[]>([]);
  /** Files whose sheet chooser is open. */
  const [openChoosers, setOpenChoosers] = useState<Set<string>>(new Set());
  const [entityInput, setEntityInput] = useState("");
  const [entityMode, setEntityMode] = useState<EntityMode>("single");
  const [fiscal, setFiscal] = useState<FiscalSettings>({ fyEndMonth: 12, ytdThrough: null });
  const [granularity, setGranularity] = useState<ColumnGranularity>("monthly");
  /** What one uploaded file holds. Decided before uploading. */
  const [fileLayout, setFileLayout] = useState<FileLayout>("month");
  const [splitMap, setSplitMap] = useState<Record<string, SplitKind>>({});
  const [dragOver, setDragOver] = useState(false);
  const [busy, setBusy] = useState<string | null>(null);
  const [errors, setErrors] = useState<string[]>([]);

  // tagging
  const [templateFileId, setTemplateFileId] = useState<string | null>(null);
  const [preview, setPreview] = useState<Preview | null>(null);
  const [headerRow, setHeaderRow] = useState(0);
  const [tags, setTags] = useState<ColumnTag[]>([]);
  const [rules, setRules] = useState<Rules>(DEFAULT_RULES);

  // review
  const [analysis, setAnalysis] = useState<AnalyzeResponse | null>(null);
  /** The exact request the analysis on screen came from, to spot edits made since. */
  const [analyzedKey, setAnalyzedKey] = useState<string | null>(null);
  const [inspecting, setInspecting] = useState<{ fileId: string; preview: Preview; headerRow: number; tags: ColumnTag[] } | null>(null);
  const [downloaded, setDownloaded] = useState(false);
  const [showSplitPanel, setShowSplitPanel] = useState(false);

  const pushError = useCallback((msg: string) => setErrors((prev) => [...prev, msg]), []);

  /** How many entity columns are currently tagged on the template. */
  const entityCount = useMemo(() => tags.filter((t) => t.role === "entity_amount").length, [tags]);

  const applyTagChange = useCallback((hr: number, next: ColumnTag[]) => {
    setHeaderRow(hr);
    setTags(next);
  }, []);
  /** Every unit the analyst has kept, in upload order. */
  const included = useMemo(() => units.filter((u) => u.include), [units]);
  // Before a template is chosen the first sheet stands in for it. Once one
  // is chosen it is never swapped for another: the header row and tags
  // belong to that sheet and would be wrong on any other.
  const templateUnit = useMemo(
    () => (templateFileId ? included.find((u) => u.id === templateFileId) ?? null : included[0] ?? null),
    [included, templateFileId],
  );

  // The chosen template left the batch (a workbook removed, its sheet
  // unticked). Its tags go with it, and the analyst picks again.
  useEffect(() => {
    if (!templateFileId || included.some((u) => u.id === templateFileId)) return;
    setTemplateFileId(null);
    setPreview(null);
    setTags([]);
    setAnalysis(null);
    setInspecting(null);
    if (phase !== "setup") {
      setPhase("setup");
      pushError("The template sheet was removed, so its tags were cleared. Tag columns again on another sheet.");
    }
  }, [templateFileId, included, phase, pushError]);
  const metaOf = useCallback((fileId: string) => fileMetas.find((m) => m.fileId === fileId) ?? null, [fileMetas]);

  const updateUnit = (id: string, patch: Partial<SheetUnit>) =>
    setUnits((prev) => prev.map((u) => (u.id === id ? { ...u, ...patch } : u)));

  /**
   * Switch what a file covers. Moving to a year kind pins the month to the
   * fiscal year end, so the analyst only ever picks the year. Choosing any
   * year kind also switches the output to fiscal columns, since monthly
   * columns cannot hold an annual figure.
   */
  const setPeriodKind = (id: string, kind: PeriodKind) => {
    setUnits((prev) => prev.map((u) => {
      if (u.id !== id) return u;
      const period = u.period && kind !== "month" ? { year: u.period.year, month: fiscal.fyEndMonth } : u.period;
      return { ...u, periodKind: kind, period };
    }));
    if (kind !== "month") setGranularity("fiscal");
  };

  /** Apply one period kind to everything at once. */
  const setAllPeriodKinds = (kind: PeriodKind) => {
    setUnits((prev) => prev.map((u) => ({
      ...u,
      periodKind: kind,
      period: u.period && kind !== "month" ? { year: u.period.year, month: fiscal.fyEndMonth } : u.period,
    })));
    setGranularity(kind === "month" ? "monthly" : "fiscal");
  };

  /**
   * Turn a sheet of a workbook on or off. Switching one on gives it the
   * period its name implies, so a sheet called "P4" arrives already dated.
   */
  const toggleSheet = (fileId: string, sheetName: string, on: boolean) => {
    const meta = metaOf(fileId);
    setUnits((prev) => {
      const id = unitId(fileId, sheetName);
      const existing = prev.find((u) => u.id === id);
      if (existing) return prev.map((u) => (u.id === id ? { ...u, include: on } : u));
      if (!on || !meta) return prev;
      const sheet = meta.sheets.find((x) => x.name === sheetName);
      const hit = parseSheetPeriod(sheetName, meta.yearHint, fiscal.fyEndMonth);
      return [...prev, {
        id, fileId, fileName: meta.name, sheetName, rows: sheet?.rows ?? 0, include: true,
        entity: entityInput.trim(), period: hit?.period ?? null, periodKind: "month",
        override: null, shift: null,
      }];
    });
  };

  const toggleChooser = (fileId: string) =>
    setOpenChoosers((prev) => {
      const next = new Set(prev);
      if (next.has(fileId)) next.delete(fileId); else next.add(fileId);
      return next;
    });

  // ── upload ──
  const uploadFiles = useCallback(async (chosen: File[]) => {
    const valid = chosen.filter((f) => /\.(xlsx|xls|csv)$/i.test(f.name));
    const skipped = chosen.filter((f) => !/\.(xlsx|xls|csv)$/i.test(f.name));
    if (valid.length === 0) { pushError("Only .xlsx, .xls and .csv files are accepted."); return; }
    setErrors([]);
    // Say what was left behind. Silently dropping files is how a month goes
    // missing without anyone noticing until the build is already wrong.
    if (skipped.length > 0) {
      pushError(`Skipped ${skipped.length} file${skipped.length === 1 ? "" : "s"} that ${skipped.length === 1 ? "is" : "are"} not .xlsx, .xls or .csv: ${skipped.map((f) => f.name).join(", ")}`);
    }
    // Sent in batches, each one into the session the first created, so a
    // large drop shows progress and one slow file cannot hold up the rest.
    let sid = sessionId;
    let read = 0;
    try {
      for (let i = 0; i < valid.length; i += UPLOAD_BATCH) {
        const batch = valid.slice(i, i + UPLOAD_BATCH);
        setBusy(valid.length > UPLOAD_BATCH
          ? `Reading files ${i + 1}–${i + batch.length} of ${valid.length}…`
          : `Reading ${valid.length} file${valid.length === 1 ? "" : "s"}…`);
        const fd = new FormData();
        fd.append("projectId", projectId);
        fd.append("entity", entityInput.trim());
        if (sid) fd.append("sessionId", sid);
        for (const f of batch) fd.append("files", f);
        const res = await apiFetch("/api/tb-aggregator/upload", { method: "POST", body: fd });
        const data = await res.json().catch(() => ({})) as Partial<UploadResponse> & { error?: string };
        for (const r of data.rejected ?? []) pushError(`${r.name}: ${r.reason}`);
        if (!res.ok) {
          if (!data.rejected?.length) pushError(data.error || `Upload failed (HTTP ${res.status})`);
          // A lost session cannot take more files; anything else is this batch only.
          if (res.status === 404) return;
          continue;
        }
        sid = data.sessionId ?? sid;
        setSessionId(sid);
        const got = data.files ?? [];
        read += got.length;
        for (const f of got) for (const note of f.skippedSheets ?? []) pushError(`${f.name}: ${note}`);
        const metas: FileMeta[] = got.map((f) => ({
          fileId: f.fileId, name: f.name, sheets: f.sheets, yearHint: f.derivedYear ?? f.derivedPeriod?.year ?? null,
        }));
        setFileMetas((prev) => [...prev, ...metas]);
        setUnits((prev) => [
          ...prev,
          ...metas.flatMap((m, j) => planUnits(m, got[j].derivedPeriod, got[j].entity, fiscal.fyEndMonth, fileLayout)),
        ]);
        setAnalysis(null);
      }
      if (read !== valid.length) {
        pushError(`Read ${read} of the ${valid.length} files sent. The ones listed above were not added.`);
      }
    } catch (err: any) {
      pushError(err?.message || "Upload failed");
    } finally {
      setBusy(null);
    }
  }, [entityInput, sessionId, projectId, fiscal.fyEndMonth, fileLayout, pushError]);

  /** Drop one sheet from the batch. The file stays, so it can be put back. */
  const removeUnit = (id: string) => {
    setUnits((prev) => prev.map((u) => (u.id === id ? { ...u, include: false } : u)));
    if (templateFileId === id) { setTemplateFileId(null); setPreview(null); setTags([]); }
  };

  /** Drop a whole workbook and everything it contributed. */
  const removeFile = async (fileId: string) => {
    setUnits((prev) => prev.filter((u) => u.fileId !== fileId));
    setFileMetas((prev) => prev.filter((m) => m.fileId !== fileId));
    if (templateUnit?.fileId === fileId) { setTemplateFileId(null); setPreview(null); setTags([]); }
    if (sessionId) {
      try { await postJson("/api/tb-aggregator/remove", { projectId, sessionId, fileId }); } catch { /* session gone — nothing to clean */ }
    }
  };

  /** Clear everything: the files, the tags, the review, and the server's copy. */
  const startOver = () => {
    if (!window.confirm("Start over? Every uploaded file, tag and check on this screen is cleared.")) return;
    if (sessionId) void postJson("/api/tb-aggregator/discard", { projectId, sessionId }).catch(() => undefined);
    setPhase("setup");
    setSessionId(null);
    setFileMetas([]);
    setUnits([]);
    setOpenChoosers(new Set());
    setSplitMap({});
    setErrors([]);
    setTemplateFileId(null);
    setPreview(null);
    setHeaderRow(0);
    setTags([]);
    setRules(DEFAULT_RULES);
    setAnalysis(null);
    setAnalyzedKey(null);
    setInspecting(null);
    setDownloaded(false);
    setShowSplitPanel(false);
  };

  // ── preview loading ──
  const loadPreview = useCallback(async (fileId: string, sheetName: string): Promise<Preview | null> => {
    if (!sessionId) return null;
    try {
      return await postJson<Preview>("/api/tb-aggregator/preview", { projectId, sessionId, fileId, sheetName });
    } catch (err: any) {
      pushError(err?.message || "Could not load the sheet preview.");
      return null;
    }
  }, [sessionId, projectId, pushError]);

  /** Load the template file's preview and seed header row + tag guesses. */
  const openTemplate = useCallback(async (fileId: string, sheetName: string) => {
    setBusy("Loading sheet…");
    const pv = await loadPreview(fileId, sheetName);
    setBusy(null);
    if (!pv) return;
    setPreview(pv);
    setHeaderRow(pv.headerRowGuess);
    setTags(guessTags(pv.rows[pv.headerRowGuess] ?? [], pv.headerRowGuess, pv.periodByCell));
  }, [loadPreview]);

  const goTagging = async () => {
    setErrors([]);
    const target = templateUnit;
    if (!target) return;
    setPhase("tagging");
    if (!preview || templateFileId !== target.id) {
      setTemplateFileId(target.id);
      await openTemplate(target.fileId, target.sheetName);
    }
  };

  /** Point the template at a different sheet of the batch. */
  const changeTemplateUnit = async (id: string) => {
    const u = included.find((x) => x.id === id);
    if (!u) return;
    setTemplateFileId(id);
    await openTemplate(u.fileId, u.sheetName);
  };

  // Debit/Credit tagged without a net column → formula applies. Default to minus.
  const hasDebitCredit = tags.some((t) => t.role === "debit") && tags.some((t) => t.role === "credit");
  const hasAmount = tags.some((t) => t.role === "amount");
  const formulaApplies = hasDebitCredit && !hasAmount && !tags.some((t) => t.role === "period_amount");
  useEffect(() => {
    if (formulaApplies && rules.netFormula === "amount") setRules((r) => ({ ...r, netFormula: "debit_minus_credit" }));
    if (!formulaApplies && rules.netFormula !== "amount") setRules((r) => ({ ...r, netFormula: "amount" }));
  }, [formulaApplies, rules.netFormula]);

  // ── analyze / build ──
  const buildRequest = useCallback((overrideUnits?: SheetUnit[]) => {
    if (!sessionId || !templateUnit) return null;
    const list = (overrideUnits ?? units).filter((u) => u.include);
    return {
      projectId,
      sessionId,
      template: { fileId: templateUnit.fileId, sheetName: templateUnit.sheetName, headerRow, tags },
      files: list.map((u) => ({
        id: u.id, fileId: u.fileId, entity: u.entity, period: u.period, sheetName: u.sheetName || null,
        override: u.override, shift: u.shift, periodKind: u.periodKind,
      })),
      rules,
      entityMode,
      fiscal,
      granularity,
      splitMap,
    };
  }, [projectId, sessionId, templateUnit, units, headerRow, tags, rules, entityMode, fiscal, granularity, splitMap]);

  const runAnalyze = useCallback(async (overrideUnits?: SheetUnit[]) => {
    const body = buildRequest(overrideUnits);
    if (!body) return;
    setErrors([]);
    setBusy("Matching columns across files…");
    try {
      const data = await postJson<AnalyzeResponse>("/api/tb-aggregator/analyze", body);
      setAnalysis(data);
      setAnalyzedKey(JSON.stringify(body));
      setDownloaded(false);
    } catch (err: any) {
      pushError(err?.message || "Analysis failed");
    } finally {
      setBusy(null);
    }
  }, [buildRequest, pushError]);

  /**
   * True when anything the build would send has changed since the analysis
   * on screen was run — an IS/BS choice not yet applied, a period edited.
   * The checks shown would then describe a different TB from the one built.
   */
  const currentKey = useMemo(() => {
    const body = buildRequest();
    return body ? JSON.stringify(body) : null;
  }, [buildRequest]);
  const stale = !!analysis && analyzedKey !== currentKey;

  const applyToAll = async () => {
    const problem = templateProblem(tags, entityMode);
    if (problem) { setErrors([problem]); return; }
    setPhase("review");
    setInspecting(null);
    await runAnalyze();
  };

  /** Accept a suggested column shift for one or more sheets and re-analyze. */
  const applyShift = async (unitIds: string[], offset: number) => {
    const ids = new Set(unitIds);
    const next = units.map((u) => (ids.has(u.id) ? { ...u, shift: offset, override: null } : u));
    setUnits(next);
    setInspecting(null);
    await runAnalyze(next);
  };

  const clearShift = async (id: string) => {
    const next = units.map((u) => (u.id === id ? { ...u, shift: null } : u));
    setUnits(next);
    await runAnalyze(next);
  };

  const build = async () => {
    const body = buildRequest();
    if (!body || stale) return;
    setErrors([]);
    setBusy("Building the monthly TB…");
    try {
      const res = await apiFetch("/api/tb-aggregator/build", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ...body, projectName: projectName ?? "" }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        pushError(err?.error || `Build failed (HTTP ${res.status})`);
        return;
      }
      const blob = await res.blob();
      const cd = res.headers.get("Content-Disposition") || "";
      const m = cd.match(/filename="([^"]+)"/);
      downloadBlob(blob, m?.[1] || "monthly-tb.xlsx");
      setDownloaded(true);
    } catch (err: any) {
      pushError(err?.message || "Build failed");
    } finally {
      setBusy(null);
    }
  };

  // ── inspect / re-tag one sheet ──
  const inspect = async (fa: FileAnalysis) => {
    const u = units.find((x) => x.id === fa.id);
    if (!u) return;
    if (fa.id === templateUnit?.id) {
      // Editing the template is editing the tagging step.
      setPhase("tagging");
      return;
    }
    setBusy("Loading sheet…");
    const pv = await loadPreview(u.fileId, u.sheetName);
    setBusy(null);
    if (!pv) return;
    setInspecting({ fileId: u.id, preview: pv, headerRow: fa.headerRow, tags: fa.tags });
  };

  const saveInspect = async () => {
    if (!inspecting) return;
    const next = units.map((u) => (u.id === inspecting.fileId
      ? { ...u, override: { headerRow: inspecting.headerRow, tags: inspecting.tags }, shift: null }
      : u));
    setUnits(next);
    setInspecting(null);
    await runAnalyze(next);
  };

  const clearOverride = async (id: string) => {
    const next = units.map((u) => (u.id === id ? { ...u, override: null, shift: null } : u));
    setUnits(next);
    setInspecting(null);
    await runAnalyze(next);
  };

  /** Leave a sheet out from the review step and re-run without it. */
  const removeFromReview = async (id: string) => {
    if (id === templateUnit?.id) {
      pushError("That sheet is the template. Choose another template sheet on Tag columns before removing it.");
      return;
    }
    const next = units.map((u) => (u.id === id ? { ...u, include: false } : u));
    setUnits(next);
    setInspecting(null);
    if (next.some((u) => u.include)) await runAnalyze(next);
    else setPhase("setup");
  };

  // ── derived ──
  const blockers = analysis ? analysis.files.filter((f) => f.status === "error" || f.status === "unmatched") : [];
  const fiscalBlocked = granularity === "fiscal" && !!analysis && analysis.unclassifiedRows > 0;
  const canBuild = !!analysis && analysis.rowCount > 0 && blockers.length === 0 && !fiscalBlocked && !busy && !stale;

  const unitById = useMemo(() => new Map(units.map((u) => [u.id, u])), [units]);
  /** How many sheets of a workbook are in use, for the chooser button. */
  const usedSheetCount = useCallback(
    (fileId: string) => units.filter((u) => u.fileId === fileId && u.include).length,
    [units],
  );

  /** Included units in the shape the period list understands. */
  const rowFiles = useMemo(
    () => included.map((u) => ({
      fileId: u.id, name: `${u.fileName} ${u.sheetName}`, entity: u.entity,
      period: u.period, periodKind: u.periodKind,
    })),
    [included],
  );
  const displayRows = useMemo(
    () => buildDisplayRows(rowFiles, entityMode, fiscal.fyEndMonth),
    [rowFiles, entityMode, fiscal.fyEndMonth],
  );
  const coverage = useMemo(
    () => coverageSummary(rowFiles, displayRows, fiscal.fyEndMonth),
    [rowFiles, displayRows, fiscal.fyEndMonth],
  );
  /** True once any file states a year rather than a month. */
  const anyYearFiles = useMemo(() => included.some((u) => u.periodKind !== "month"), [included]);

  /**
   * The year the part-year column belongs to. Taken from "Data runs through"
   * when set, otherwise the latest year the files carry.
   */
  const ytdYear = useMemo(() => {
    const t = fiscal.ytdThrough;
    if (t) return t.month <= fiscal.fyEndMonth ? t.year : t.year + 1;
    const years = included.map((u) => u.period?.year).filter((y): y is number => typeof y === "number");
    return years.length ? Math.max(...years) : THIS_YEAR;
  }, [fiscal.ytdThrough, fiscal.fyEndMonth, included]);

  /** Files sharing the same suggested offset, so one click can fix the whole group. */
  const shiftGroups = useMemo(() => {
    if (!analysis) return [] as { offset: number; fileIds: string[] }[];
    const byOffset = new Map<number, string[]>();
    for (const f of analysis.files) {
      if (!f.suggestedShift) continue;
      const o = f.suggestedShift.offset;
      if (!byOffset.has(o)) byOffset.set(o, []);
      (byOffset.get(o) as string[]).push(f.id);
    }
    return [...byOffset.entries()]
      .map(([offset, fileIds]) => ({ offset, fileIds }))
      .sort((a, b) => b.fileIds.length - a.fileIds.length);
  }, [analysis]);

  // ── render ──
  return (
    <section style={{ border: "1px solid var(--theme-border)", borderRadius: 10, background: "var(--theme-tint-3)", padding: 12, display: "flex", flexDirection: "column", gap: 12 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
        <div style={{ fontWeight: 700, color: GREEN, fontSize: 13 }}>TB Aggregator</div>
        <div style={{ display: "flex", gap: 4, fontSize: 11 }}>
          {(["setup", "tagging", "review"] as Phase[]).map((p, i) => (
            <span key={p} style={{ padding: "2px 8px", borderRadius: 10, background: phase === p ? "var(--theme-accent)" : "var(--theme-tint-19)", color: phase === p ? "#fff" : "var(--theme-ink-soft)", fontWeight: 600 }}>
              {i + 1}. {p === "setup" ? "Files" : p === "tagging" ? "Tag columns" : "Review & build"}
            </span>
          ))}
        </div>
        {busy && <span style={{ ...hint, fontStyle: "italic", color: "var(--theme-ink-soft)" }}>{busy}</span>}
        {(units.length > 0 || sessionId) && (
          <button onClick={startOver} disabled={!!busy} style={{ ...dangerBtn, marginLeft: "auto" }} title="Clear every file and tag and begin again">
            Start over
          </button>
        )}
      </div>

      {errors.length > 0 && (
        <div style={{ background: "#fef2f2", border: "1px solid #fecaca", borderRadius: 8, padding: "8px 10px", display: "flex", flexDirection: "column", gap: 4 }}>
          {errors.map((e, i) => <div key={i} style={{ fontSize: 12, color: "#b91c1c" }}>{e}</div>)}
        </div>
      )}

      {/* ── 1. setup ── */}
      {phase === "setup" && (
        <>
          <div style={card}>
            <div style={h2}>Add trial balance files</div>
            <div style={hint}>
              Drop every monthly TB in. The month is read from each filename and shown below for you to confirm.
            </div>

            <div style={{ display: "flex", gap: 14, flexWrap: "wrap", padding: "6px 10px", background: "var(--theme-tint-7)", border: "1px solid var(--theme-tint-22)", borderRadius: 6 }}>
              <label style={{ fontSize: 12, display: "flex", gap: 6, alignItems: "flex-start", flex: "1 1 210px" }}>
                <input type="radio" checked={entityMode === "single"} onChange={() => setEntityMode("single")} style={{ marginTop: 2 }} />
                <span>
                  <strong style={{ color: GREEN }}>One entity per file</strong>
                  <div style={hint}>Each TB covers a single company. You name it.</div>
                </span>
              </label>
              <label style={{ fontSize: 12, display: "flex", gap: 6, alignItems: "flex-start", flex: "1 1 260px" }}>
                <input type="radio" checked={entityMode === "spread"} onChange={() => setEntityMode("spread")} style={{ marginTop: 2 }} />
                <span>
                  <strong style={{ color: GREEN }}>Several entities per file</strong>
                  <div style={hint}>Amounts spread across columns, each headed by a company name. You mark the first and last.</div>
                </span>
              </label>
            </div>

            <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap", opacity: entityMode === "single" ? 1 : 0.5 }}>
              <label style={{ fontSize: 12, fontWeight: 600, color: entityMode === "single" ? GREEN : "#9ca3af" }}>Entity</label>
              <input
                value={entityMode === "single" ? entityInput : ""}
                onChange={(e) => setEntityInput(e.target.value)}
                disabled={entityMode !== "single"}
                placeholder={entityMode === "single" ? "e.g. ABC Holdings LLC" : "read from the column headings"}
                style={{ ...inputStyle, width: 260, background: entityMode === "single" ? "#fff" : "#f3f4f6", cursor: entityMode === "single" ? "text" : "not-allowed" }}
              />
              <span style={hint}>
                {entityMode === "single"
                  ? "Applied to the files you add next. Add another batch to cover a second entity."
                  : "Not needed — each entity is named by its own column heading."}
              </span>
            </div>

            {/* What one file holds. Decided before uploading, so each
                workbook's sheets can be placed without guessing. */}
            <div style={{ display: "flex", gap: 14, flexWrap: "wrap", padding: "6px 10px", background: "var(--theme-tint-7)", border: "1px solid var(--theme-tint-22)", borderRadius: 6 }}>
              {([
                ["month", "One month per file", "The usual VDR dump: a file per month."],
                ["fy", "One fiscal year per file", "Annual trial balances, one per year."],
                ["multi", "Several months in each file", "A workbook with a sheet per month, named P1, P2 or Jan, Feb."],
              ] as const).map(([v, title, note]) => (
                <label key={v} style={{ fontSize: 12, display: "flex", gap: 6, alignItems: "flex-start", flex: "1 1 200px" }}>
                  <input
                    type="radio"
                    checked={fileLayout === v}
                    onChange={() => { setFileLayout(v); if (v === "fy") setGranularity("fiscal"); }}
                    style={{ marginTop: 2 }}
                  />
                  <span>
                    <strong style={{ color: GREEN }}>{title}</strong>
                    <div style={hint}>{note}</div>
                  </span>
                </label>
              ))}
            </div>
            {units.length > 0 && (
              <div style={{ ...hint, color: "#b45309" }}>
                Changing this after uploading only affects files added from now on. Remove and re-add anything already listed.
              </div>
            )}

            {/* Fiscal year + column granularity. */}
            <div style={{ display: "flex", gap: 14, flexWrap: "wrap", alignItems: "flex-start", padding: "6px 10px", background: "#fffdf4", border: "1px solid #f1e6b3", borderRadius: 6 }}>
              <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                <span style={{ fontSize: 11, fontWeight: 700, color: "#7c5e10" }}>Fiscal year starts</span>
                {/* Asked as the start month, since that is how a year is
                    described. Kept as the end month underneath: a year is
                    always twelve months, so the one gives the other. */}
                <span style={{ fontSize: 12, display: "inline-flex", gap: 6, alignItems: "center" }}>
                  <select
                    value={(fiscal.fyEndMonth % 12) + 1}
                    onChange={(e) => {
                      const start = parseInt(e.target.value, 10);
                      setFiscal((f) => ({ ...f, fyEndMonth: start === 1 ? 12 : start - 1 }));
                    }}
                    style={selectStyle}
                  >
                    {MONTHS.map((m, i) => <option key={m} value={i + 1}>{m}</option>)}
                  </select>
                  <span style={hint}>ends {MONTHS[fiscal.fyEndMonth - 1]}</span>
                </span>
                <span style={{ ...hint, maxWidth: 230 }}>
                  Twelve months from the month you pick. A year is named by the year it ends in: FY{String(THIS_YEAR).slice(2)} runs {MONTHS[fiscal.fyEndMonth % 12]}-{String(fiscal.fyEndMonth === 12 ? THIS_YEAR : THIS_YEAR - 1).slice(2)} to {MONTHS[fiscal.fyEndMonth - 1]}-{String(THIS_YEAR).slice(2)}.
                </span>
              </div>

              <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                <span style={{ fontSize: 11, fontWeight: 700, color: "#7c5e10" }}>Data runs through</span>
                <PeriodPicker value={fiscal.ytdThrough} onChange={(p) => setFiscal((f) => ({ ...f, ytdThrough: p }))} />
                <span style={{ ...hint, maxWidth: 230 }}>
                  The last month you have. A year stopping short of {MONTHS[fiscal.fyEndMonth - 1]} is labelled YTD. Leave blank to take it from the files.
                </span>
              </div>

              <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                <span style={{ fontSize: 11, fontWeight: 700, color: "#7c5e10" }}>Columns in the built TB</span>
                {([["monthly", "One per month (Jan-24, Feb-24, …)"], ["fiscal", "One per fiscal year (FY24, FY25, YTD-26)"]] as const).map(([v, text]) => (
                  <label key={v} style={{ fontSize: 12, display: "flex", gap: 6, alignItems: "center" }}>
                    <input type="radio" checked={granularity === v} onChange={() => setGranularity(v)} />
                    {text}
                  </label>
                ))}
                {granularity === "fiscal" && (
                  <span style={{ ...hint, maxWidth: 280 }}>
                    Income statement accounts are summed across the year. Balance sheet accounts take the closing balance, so every account has to be classified first.
                  </span>
                )}
              </div>
            </div>
            <div
              onDrop={(e) => {
                e.preventDefault();
                setDragOver(false);
                // One upload at a time: a second drop mid-upload would race
                // the first for the session.
                if (busy) return;
                void uploadFiles(Array.from(e.dataTransfer?.files || []));
              }}
              onDragOver={(e) => { e.preventDefault(); if (!busy) setDragOver(true); }}
              onDragLeave={() => setDragOver(false)}
              style={{ padding: 20, border: `2px dashed ${dragOver ? "var(--theme-action)" : "var(--theme-accent)"}`, background: dragOver ? "var(--theme-tint-21)" : "var(--theme-tint-7)", color: GREEN, borderRadius: 10, textAlign: "center", fontSize: 13, cursor: "pointer" }}
            >
              <label style={{ cursor: busy ? "progress" : "pointer", display: "block" }}>
                <input type="file" multiple accept={ACCEPT} disabled={!!busy} onChange={(e) => { void uploadFiles(Array.from(e.target.files || [])); e.currentTarget.value = ""; }} style={{ display: "none" }} />
                {busy ? (
                  <>
                    <div style={{ fontWeight: 700, marginBottom: 4 }}>{busy}</div>
                    <div style={{ fontSize: 12, color: "var(--theme-ink-soft)" }}>Large workbooks take a moment. They will appear in the list below.</div>
                  </>
                ) : (
                  <>
                    <div style={{ fontWeight: 700, marginBottom: 4 }}>Drop trial balance files here, or click to choose</div>
                    <div style={{ fontSize: 12, color: "var(--theme-ink-soft)" }}>Accepts .xlsx, .xls, .csv. Many at once.</div>
                  </>
                )}
              </label>
            </div>
          </div>

          {units.length > 0 && (
            <div style={card}>
              <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
                <div style={h2}>Periods ({included.length})</div>
                <span style={hint}>
                  from {fileMetas.length} file{fileMetas.length === 1 ? "" : "s"}
                </span>
                {coverage.span && <span style={{ fontSize: 12, color: "#374151" }}>{coverage.span}</span>}
                {coverage.gaps > 0 && (
                  <span style={{ fontSize: 11, padding: "1px 8px", borderRadius: 10, background: "#fef3c7", color: "#b45309", fontWeight: 700 }}>
                    {coverage.gaps} month{coverage.gaps === 1 ? "" : "s"} with no file
                  </span>
                )}
                {coverage.undated > 0 && (
                  <span style={{ fontSize: 11, padding: "1px 8px", borderRadius: 10, background: "#fef3c7", color: "#b45309", fontWeight: 700 }}>
                    {coverage.undated} without a period
                  </span>
                )}
                <span style={{ marginLeft: "auto", display: "inline-flex", gap: 6, alignItems: "center" }}>
                  <span style={hint}>Every file is a</span>
                  <select
                    value=""
                    onChange={(e) => { if (e.target.value) setAllPeriodKinds(e.target.value as PeriodKind); }}
                    style={{ ...selectStyle, width: 116 }}
                    title="Set what every file covers in one go"
                  >
                    <option value="">set all…</option>
                    <option value="month">Month</option>
                    <option value="fy">Fiscal year</option>
                  </select>
                </span>
                {coverage.gaps === 0 && coverage.undated === 0 && included.length > 0 && (
                  <span style={{ fontSize: 11, padding: "1px 8px", borderRadius: 10, background: "#dcfce7", color: "var(--theme-action)", fontWeight: 700 }}>no gaps</span>
                )}
              </div>
              {/* Every workbook, always visible, so an uploaded file can never
                  vanish and the sheet chooser is always within reach. */}
              <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                {fileMetas.map((m) => {
                  const used = usedSheetCount(m.fileId);
                  return (
                    <span key={m.fileId} style={{ display: "inline-flex", alignItems: "center", gap: 6, fontSize: 11, padding: "2px 8px", borderRadius: 12, background: used > 0 ? "#f0fdf4" : "#fffbeb", border: `1px solid ${used > 0 ? "#bbf7d0" : "#fde68a"}`, color: used > 0 ? "var(--theme-action)" : "#b45309" }}>
                      <span style={{ fontFamily: "monospace", maxWidth: 220, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} title={m.name}>{m.name}</span>
                      <button onClick={() => toggleChooser(m.fileId)} style={{ ...ghostBtn, padding: "0 5px", fontSize: 10 }} title="Choose which sheets of this workbook to use">
                        {used} of {m.sheets.length} sheets
                      </button>
                      <button onClick={() => void removeFile(m.fileId)} style={{ ...dangerBtn, padding: "0 5px", fontSize: 10 }} title="Remove this workbook">✕</button>
                    </span>
                  );
                })}
              </div>
              {included.length === 0 && (
                <div style={{ fontSize: 12, padding: "6px 10px", borderRadius: 6, background: "#fffbeb", border: "1px solid #fde68a", color: "#b45309" }}>
                  No sheets are in use yet. Open a workbook's sheet list above and tick the sheets that are trial balances.
                  {fileLayout === "multi" && " Sheet names like P1 also need a year in the filename, such as \"TB FY2024.xlsx\", to know which year they belong to."}
                </div>
              )}
              <div style={hint}>
                Sorted oldest to newest{entityMode === "single" ? ", grouped by entity" : ""}. Any period with no file is called out in the list.
                {anyYearFiles
                  ? " A file set to a fiscal year or year to date needs only its year, and is never checked for missing months."
                  : " Fix any month the filename didn't give away. Leave the period blank only when the file has months as columns."}
              </div>
              <div style={{ overflowX: "auto", border: "1px solid #e5e7eb", borderRadius: 6 }}>
                <table style={{ borderCollapse: "collapse", width: "100%" }}>
                  <thead>
                    <tr>
                      <th style={th}>#</th>
                      <th style={th}>File</th>
                      <th style={th}>Sheet</th>
                      {entityMode === "single" && <th style={th}>Entity</th>}
                      <th style={th}>Month</th>
                      <th style={th}></th>
                    </tr>
                  </thead>
                  <tbody>
                    {displayRows.map((row, ri) => {
                      if (row.kind === "gap") {
                        return (
                          <tr key={`gap-${row.label}-${ri}`} style={{ background: "#fffbeb" }}>
                            <td style={{ ...td, color: "#b45309" }}>—</td>
                            <td colSpan={entityMode === "single" ? 5 : 4} style={{ ...td, color: "#b45309", fontWeight: 600 }}>
                              {row.label} — no file for this {row.label.startsWith("FY") ? "year" : "month"}
                            </td>
                          </tr>
                        );
                      }
                      const u = unitById.get(row.file.fileId);
                      if (!u) return null;
                      const f = { ...row.file, ...u };
                      const sheetCount = metaOf(u.fileId)?.sheets.length ?? 1;
                      return (
                        <tr key={u.id} style={{ background: !u.period ? "#fffbeb" : row.duplicate ? "#fff7ed" : row.n % 2 ? "#fcfcfc" : "#fff" }}>
                          <td style={{ ...td, color: "#9ca3af" }}>{row.n}</td>
                          <td style={{ ...td, fontFamily: "monospace", maxWidth: 300, overflow: "hidden", textOverflow: "ellipsis" }} title={u.fileName}>{u.fileName}</td>
                          <td style={td}>
                            <span>{u.sheetName} <span style={{ color: "#9ca3af" }}>({u.rows} rows)</span></span>
                            {sheetCount > 1 && (
                              <button onClick={() => toggleChooser(u.fileId)} style={{ ...ghostBtn, marginLeft: 6, padding: "0 5px", fontSize: 10 }} title="Choose which sheets of this workbook to use">
                                {usedSheetCount(u.fileId)} of {sheetCount}
                              </button>
                            )}
                          </td>
                          {entityMode === "single" && (
                            <td style={td}><input value={f.entity} onChange={(e) => updateUnit(u.id, { entity: e.target.value })} style={{ ...inputStyle, width: 160, fontSize: 11, padding: "2px 4px" }} /></td>
                          )}
                          <td style={td}>
                            <span style={{ display: "inline-flex", alignItems: "center", gap: 6, flexWrap: "wrap" }}>
                              <select
                                value={f.periodKind === "month" ? "month" : "fy"}
                                onChange={(e) => setPeriodKind(u.id, e.target.value === "month" ? "month" : "fy")}
                                title="What this file covers"
                                style={{ ...selectStyle, width: 94 }}
                              >
                                <option value="month">Month</option>
                                <option value="fy">Fiscal year</option>
                              </select>
                              {f.periodKind === "month" ? (
                                <PeriodPicker value={f.period} onChange={(p) => updateUnit(u.id, { period: p })} />
                              ) : (
                                // A year file needs only its year. The part year sits in the
                                // same list, so choosing YTD is one click rather than a mode.
                                <select
                                  value={f.period ? `${f.periodKind}:${f.period.year}` : ""}
                                  onChange={(e) => {
                                    const v = e.target.value;
                                    if (!v) { updateUnit(u.id, { period: null }); return; }
                                    const [kind, yr] = v.split(":");
                                    updateUnit(u.id, {
                                      periodKind: kind as PeriodKind,
                                      period: { year: parseInt(yr, 10), month: fiscal.fyEndMonth },
                                    });
                                  }}
                                  style={{ ...selectStyle, width: 104, borderColor: f.period ? "var(--theme-border)" : "#f59e0b" }}
                                >
                                  <option value="">—</option>
                                  <optgroup label="Full year">
                                    {YEARS.map((y) => <option key={y} value={`fy:${y}`}>FY{String(y).slice(2)}</option>)}
                                  </optgroup>
                                  <optgroup label="Part year">
                                    <option value={`ytd:${ytdYear}`}>YTD-{String(ytdYear).slice(2)}</option>
                                  </optgroup>
                                </select>
                              )}
                            </span>
                            {!f.period && f.periodKind === "month" && <span style={{ marginLeft: 6, color: "#b45309", fontSize: 10 }}>not in filename</span>}
                            {row.duplicate && (
                              <span style={{ marginLeft: 6, color: "#b45309", fontSize: 10 }}>
                                same {f.periodKind === "month" ? "month" : "year"} as another file
                              </span>
                            )}
                          </td>
                          <td style={td}><button onClick={() => removeUnit(u.id)} style={dangerBtn} title="Leave this sheet out">✕</button></td>
                        </tr>
                      );
                    })}
                    {/* Sheet choosers, one per workbook the analyst opened. */}
                    {[...openChoosers].map((fileId) => {
                      const meta = metaOf(fileId);
                      if (!meta) return null;
                      return (
                        <tr key={`chooser-${fileId}`} style={{ background: "#f8fbff" }}>
                          <td style={td}></td>
                          <td colSpan={entityMode === "single" ? 5 : 4} style={{ ...td, whiteSpace: "normal" }}>
                            <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap", marginBottom: 4 }}>
                              <strong style={{ color: "#1d4ed8", fontSize: 11 }}>Sheets in {meta.name}</strong>
                              <span style={hint}>Tick the sheets that are trial balances. Each one becomes its own period.</span>
                              <button onClick={() => toggleChooser(fileId)} style={ghostBtn}>Done</button>
                            </div>
                            <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
                              {meta.sheets.map((sh) => {
                                const on = units.some((u) => u.id === unitId(fileId, sh.name) && u.include);
                                return (
                                  <label key={sh.name} style={{ fontSize: 11, display: "inline-flex", gap: 4, alignItems: "center", color: on ? GREEN : "#6b7280" }}>
                                    <input type="checkbox" checked={on} onChange={(e) => toggleSheet(fileId, sh.name, e.target.checked)} />
                                    {sh.name} <span style={{ color: "#9ca3af" }}>({sh.rows})</span>
                                  </label>
                                );
                              })}
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              {(() => {
                const needsEntity = entityMode === "single" && included.some((f) => !f.entity.trim());
                return (
                  <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                    <button onClick={() => void goTagging()} disabled={!!busy || needsEntity} style={primaryBtn(!!busy || needsEntity)}>Next: tag columns →</button>
                    {needsEntity && <span style={{ ...hint, color: "#b45309" }}>Every file needs an entity name.</span>}
                  </div>
                );
              })()}
            </div>
          )}
        </>
      )}

      {/* ── 2. tagging ── */}
      {phase === "tagging" && templateUnit && (
        <>
          <div style={card}>
            <div style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
              <div style={h2}>Tag columns on one file</div>
              <label style={{ fontSize: 11, color: "#6b7280" }}>Template sheet</label>
              <select value={templateUnit.id} onChange={(e) => void changeTemplateUnit(e.target.value)} style={{ ...selectStyle, maxWidth: 420 }}>
                {included.map((u) => (
                  <option key={u.id} value={u.id}>
                    {u.fileName}{(metaOf(u.fileId)?.sheets.length ?? 1) > 1 ? ` › ${u.sheetName}` : ""}
                  </option>
                ))}
              </select>
            </div>
            <div style={hint}>
              Click the row number that holds the column headers, then pick what each column is. The same headers are then found in every other
              file by name, wherever they sit. Tag only what you can — Account #, Account Name and an Amount (or Debit + Credit) are the minimum.
              {entityMode !== "single" && " Tag the Entity column too: a name written once above a block of accounts covers that block until the next name appears."}
            </div>
            {entityMode === "spread" && (
              <div style={{ fontSize: 12, padding: "6px 10px", borderRadius: 6, background: entityCount > 0 ? "#f0fdf4" : "#fffbeb", border: `1px solid ${entityCount > 0 ? "#bbf7d0" : "#fde68a"}`, color: entityCount > 0 ? "var(--theme-action)" : "#b45309" }}>
                {entityCount > 0
                  ? <><strong>{entityCount} entity columns tagged.</strong> Their headings are the entity names, and those columns hold the amounts — there is no separate Amount or Debit/Credit column to tag.</>
                  : <>Use <strong>Tag a range</strong> below to mark the first and last entity column. Their headings become the entity names, and those columns hold the amounts.</>}
              </div>
            )}
            {preview ? (
              <SheetTagger preview={preview} headerRow={headerRow} tags={tags} onChange={applyTagChange} />
            ) : (
              <div style={hint}>Loading…</div>
            )}
            {tags.length > 0 && (
              <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                {tags.map((t) => (
                  <span key={`${t.role}-${t.col}`} style={{ fontSize: 10, padding: "2px 6px", borderRadius: 10, background: "var(--theme-tint-19)", color: GREEN, fontWeight: 600 }}>
                    {ROLE_LABEL[t.role]}{t.period ? ` ${periodLabel(t.period)}` : ""} ← {t.label || `col ${t.col + 1}`}
                  </span>
                ))}
              </div>
            )}
          </div>

          <div style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
            {formulaApplies && (
              <div style={{ ...card, flex: "1 1 260px" }}>
                <div style={h2}>Net column</div>
                <div style={hint}>Debit and Credit are tagged. The net amount for each row is computed as:</div>
                {(["debit_minus_credit", "debit_plus_credit"] as NetFormula[]).map((f) => (
                  <label key={f} style={{ fontSize: 12, display: "flex", gap: 6, alignItems: "center" }}>
                    <input type="radio" checked={rules.netFormula === f} onChange={() => setRules((r) => ({ ...r, netFormula: f }))} />
                    {f === "debit_minus_credit" ? "Debit − Credit (credits negative)" : "Debit + Credit (credits already signed)"}
                  </label>
                ))}
              </div>
            )}
            <div style={{ ...card, flex: "2 1 320px" }}>
              <div style={h2}>Exclude rows</div>
              <label style={{ fontSize: 12, display: "flex", gap: 6, alignItems: "center" }}>
                <input type="checkbox" checked={rules.excludeBlankAccountNumber} onChange={(e) => setRules((r) => ({ ...r, excludeBlankAccountNumber: e.target.checked }))} />
                Blank Account # <span style={hint}>(subtotals, section headers, spacer rows)</span>
              </label>
              <label style={{ fontSize: 12, display: "flex", gap: 6, alignItems: "center" }}>
                <input type="checkbox" checked={rules.excludeTotalRows} onChange={(e) => setRules((r) => ({ ...r, excludeTotalRows: e.target.checked }))} />
                Account Name contains "total"
              </label>
              <label style={{ fontSize: 12, display: "flex", gap: 6, alignItems: "center" }}>
                <input type="checkbox" checked={rules.excludeZeroRows} onChange={(e) => setRules((r) => ({ ...r, excludeZeroRows: e.target.checked }))} />
                All amounts blank or zero <span style={hint}>(off by default — a zero month would otherwise look like a missing account)</span>
              </label>
              <label style={{ fontSize: 12, display: "flex", gap: 6, alignItems: "center" }}>
                Account Name contains
                <input value={rules.customExcludeText} onChange={(e) => setRules((r) => ({ ...r, customExcludeText: e.target.value }))} placeholder="custom text" style={{ ...inputStyle, width: 180, fontSize: 11, padding: "2px 6px" }} />
              </label>
            </div>
          </div>

          <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
            <button onClick={() => setPhase("setup")} style={ghostBtn}>← Files</button>
            <button onClick={() => void applyToAll()} disabled={!!busy || !preview} style={primaryBtn(!!busy || !preview)}>Apply to all {included.length} files →</button>
            {templateProblem(tags, entityMode) && <span style={{ ...hint, color: "#b45309" }}>{templateProblem(tags, entityMode)}</span>}
          </div>
        </>
      )}

      {/* ── 3. review ── */}
      {phase === "review" && (
        <>
          {analysis && (
            <div style={card}>
              <div style={{ display: "flex", gap: 14, alignItems: "baseline", flexWrap: "wrap" }}>
                <div style={h2}>Result</div>
                {(() => {
                  const ents = new Set<string>();
                  for (const f of analysis.files) for (const e of f.entitiesFound) ents.add(e);
                  const labels = analysis.columnLabels;
                  return (
                    <span style={{ fontSize: 12, color: "#374151" }}>
                      <strong>{analysis.rowCount}</strong> account rows · <strong>{labels.length}</strong> {granularity === "fiscal" ? "period" : "month"}{labels.length === 1 ? "" : "s"}
                      {labels.length > 0 && <> ({labels[0]} to {labels[labels.length - 1]})</>}
                      {" · "}<strong>{ents.size}</strong> entit{ents.size === 1 ? "y" : "ies"}
                    </span>
                  );
                })()}
                {(["matched", "manual", "partial", "unmatched", "error"] as FileStatus[]).map((s) => {
                  const n = analysis.files.filter((f) => f.status === s).length;
                  if (!n) return null;
                  return <span key={s} style={{ fontSize: 11, padding: "1px 8px", borderRadius: 10, background: STATUS_STYLE[s].bg, color: STATUS_STYLE[s].fg, fontWeight: 700 }}>{n} {STATUS_STYLE[s].text.toLowerCase()}</span>;
                })}
              </div>

              {/* IS/BS classification. Shown whenever anything is unplaced, or
                  the analyst opens it, since fiscal columns depend on it. */}
              {analysis.splitValues.length > 0 && (analysis.splitNeedsInput || granularity === "fiscal" || showSplitPanel) && (
                <div style={{ background: analysis.splitNeedsInput ? "#fffbeb" : "#f9fafb", border: `1px solid ${analysis.splitNeedsInput ? "#fde68a" : "#e5e7eb"}`, borderRadius: 6, padding: "8px 10px", display: "flex", flexDirection: "column", gap: 6 }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
                    <span style={{ fontSize: 12, fontWeight: 700, color: analysis.splitNeedsInput ? "#b45309" : "#374151" }}>
                      Income statement or balance sheet
                    </span>
                    <span style={hint}>
                      {analysis.splitSource === "split_column"
                        ? "Read from the IS/BS column you tagged."
                        : "No IS/BS column was tagged, so this is read from Mapping 1."}
                      {" "}Set anything marked unknown. N/A drops those rows from the TB.
                    </span>
                    <button onClick={() => setShowSplitPanel((v) => !v)} style={ghostBtn}>{showSplitPanel ? "hide" : "show all"}</button>
                  </div>
                  <div style={{ overflowX: "auto", maxHeight: 260, overflowY: "auto", border: "1px solid #e5e7eb", borderRadius: 4, background: "#fff" }}>
                    <table style={{ borderCollapse: "collapse", width: "100%" }}>
                      <thead>
                        <tr>
                          <th style={th}>Mapping value</th>
                          <th style={{ ...th, textAlign: "right" }}>Rows</th>
                          <th style={th}>Side</th>
                        </tr>
                      </thead>
                      <tbody>
                        {analysis.splitValues
                          .filter((v) => showSplitPanel || v.guess === null || splitMap[splitKeyOf(v.value)] === "NA")
                          .map((v) => {
                            const k = splitKeyOf(v.value);
                            // Indexing a Record yields a non-optional type, so the
                            // lookup is widened explicitly or the fallbacks are dead.
                            const chosen = splitMap[k] as SplitKind | undefined;
                            // Plain string: the analyst's pick, else the guess,
                            // else nothing chosen yet.
                            const current: string = chosen ?? v.guess ?? "";
                            return (
                              <tr key={k} style={{ background: current === "" ? "#fffbeb" : "#fff" }}>
                                <td style={{ ...td, maxWidth: 320, overflow: "hidden", textOverflow: "ellipsis" }} title={v.value}>{v.value}</td>
                                <td style={{ ...td, textAlign: "right", color: "#6b7280" }}>{v.rows}</td>
                                <td style={td}>
                                  <span style={{ display: "inline-flex", gap: 8 }}>
                                    {(["IS", "BS", "NA"] as const).map((kind) => (
                                      <label key={kind} style={{ fontSize: 11, display: "inline-flex", gap: 3, alignItems: "center", color: kind === "NA" ? "#b91c1c" : "#374151" }}>
                                        <input
                                          type="radio"
                                          checked={current === kind}
                                          onChange={() => setSplitMap((m) => ({ ...m, [k]: kind }))}
                                        />
                                        {kind === "NA" ? "N/A" : kind}
                                      </label>
                                    ))}
                                    {current === "" && <span style={{ fontSize: 10, color: "#b45309", fontWeight: 700 }}>unknown</span>}
                                    {!chosen && v.guess && <span style={{ fontSize: 10, color: "#9ca3af" }}>auto</span>}
                                  </span>
                                </td>
                              </tr>
                            );
                          })}
                      </tbody>
                    </table>
                  </div>
                  <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
                    <button onClick={() => void runAnalyze()} disabled={!!busy} style={{ ...ghostBtn, background: "#dcfce7", borderColor: "#86efac", color: "var(--theme-action)" }}>Apply classification</button>
                    {analysis.splitExcludedRows > 0 && (
                      <span style={hint}>
                        {analysis.splitExcludedRows} rows dropped as N/A. Those files will stop netting to zero by exactly the amount removed, which is expected.
                      </span>
                    )}
                    {analysis.unclassifiedRows > 0 && granularity === "fiscal" && (
                      <span style={{ ...hint, color: "#b91c1c" }}>{analysis.unclassifiedRows} accounts still unclassified — fiscal columns cannot be built until they are set.</span>
                    )}
                  </div>
                </div>
              )}

              {/* Column-shift suggestions, grouped so one click fixes a whole batch. */}
              {shiftGroups.map((g) => (
                <div key={g.offset} style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap", background: "#eff6ff", border: "1px solid #bfdbfe", borderRadius: 6, padding: "6px 10px" }}>
                  <span style={{ fontSize: 12, color: "#1e3a8a" }}>
                    <strong>{g.fileIds.length} file{g.fileIds.length === 1 ? "" : "s"}</strong> did not match on header names, but the columns line up when moved <strong>{shiftText(g.offset)}</strong>.
                  </span>
                  <button onClick={() => void applyShift(g.fileIds, g.offset)} disabled={!!busy} style={{ ...ghostBtn, borderColor: "#93c5fd", background: "#dbeafe", color: "#1d4ed8" }}>
                    Shift {g.offset > 0 ? "+" : ""}{g.offset} on all {g.fileIds.length}
                  </button>
                  <span style={{ ...hint, color: "#1e3a8a" }}>Check the net and row counts afterwards.</span>
                </div>
              ))}

              <div style={{ overflowX: "auto", border: "1px solid #e5e7eb", borderRadius: 6 }}>
                <table style={{ borderCollapse: "collapse", width: "100%" }}>
                  <thead>
                    <tr>
                      <th style={th}>File</th>
                      <th style={th}>Entit{entityMode !== "single" ? "ies" : "y"}</th>
                      <th style={th}>Month</th>
                      <th style={th}>Sheet</th>
                      <th style={th}>Header</th>
                      <th style={th}>Status</th>
                      <th style={{ ...th, textAlign: "right" }}>Rows in</th>
                      <th style={{ ...th, textAlign: "right" }}>Excluded</th>
                      <th style={{ ...th, textAlign: "right" }}>Net</th>
                      <th style={th}>Dups</th>
                      <th style={th}></th>
                    </tr>
                  </thead>
                  <tbody>
                    {analysis.files.map((fa, i) => {
                      const st = STATUS_STYLE[fa.status];
                      const excludedCount = Object.values(fa.excluded).reduce((a, b) => a + b, 0);
                      const excludedTitle = Object.entries(fa.excluded).map(([k, v]) => `${k}: ${v}`).join("\n");
                      const isTemplate = fa.id === templateUnit?.id;
                      return (
                        <React.Fragment key={fa.id}>
                          <tr style={{ background: i % 2 ? "#fcfcfc" : "#fff" }}>
                            <td style={{ ...td, fontFamily: "monospace", maxWidth: 300, overflow: "hidden", textOverflow: "ellipsis" }} title={fa.fileName}>
                              {fa.fileName}{isTemplate && <span style={{ marginLeft: 6, fontSize: 9, color: GREEN, fontWeight: 700 }}>TEMPLATE</span>}
                            </td>
                            <td style={{ ...td, maxWidth: 200, overflow: "hidden", textOverflow: "ellipsis" }} title={fa.entitiesFound.join(", ")}>
                              {fa.entitiesFound.length === 0
                                ? <span style={{ color: "#b45309" }}>{entityMode !== "single" ? "none found" : fa.entity}</span>
                                : fa.entitiesFound.length <= 2 ? fa.entitiesFound.join(", ") : `${fa.entitiesFound.length} entities`}
                            </td>
                            <td style={td}>{fa.period ? fileLabel(fa.period, fa.periodKind) : fa.periods.length ? `${fa.periods.length} cols` : <span style={{ color: "#b45309" }}>—</span>}</td>
                            <td style={td}>{fa.sheetName}</td>
                            <td style={{ ...td, textAlign: "center" }}>{fa.status === "error" && !fa.sheetName ? "" : fa.headerRow + 1}</td>
                            <td style={td}>
                              <span style={{ fontSize: 10, padding: "1px 7px", borderRadius: 10, background: st.bg, color: st.fg, fontWeight: 700 }}>
                                {st.text}{fa.appliedShift ? ` ${fa.appliedShift > 0 ? "+" : ""}${fa.appliedShift}` : ""}
                              </span>
                              {fa.missingRoles.length > 0 && <div style={{ fontSize: 10, color: "#b45309", marginTop: 2 }}>missing: {fa.missingRoles.map((r) => ROLE_LABEL[r]).join(", ")}</div>}
                              {fa.textAmountCells > 0 && (
                                <div style={{ fontSize: 10, color: "#b45309", marginTop: 2 }} title="These cells were left out of every total. Check the file for amounts stored as text, such as 1,250 CR.">
                                  {fa.textAmountCells} amount cell{fa.textAmountCells === 1 ? "" : "s"} held text, not counted
                                </div>
                              )}
                              {fa.missingEntities.length > 0 && <div style={{ fontSize: 10, color: "#b45309", marginTop: 2 }} title={fa.missingEntities.join(", ")}>no column for: {fa.missingEntities.slice(0, 3).join(", ")}{fa.missingEntities.length > 3 ? ` +${fa.missingEntities.length - 3}` : ""}</div>}
                              {fa.suggestedShift && (
                                <div style={{ fontSize: 10, color: "#1d4ed8", marginTop: 2 }}>
                                  looks shifted {shiftText(fa.suggestedShift.offset)}{" "}
                                  <button onClick={() => void applyShift([fa.id], fa.suggestedShift!.offset)} style={{ ...ghostBtn, padding: "0 5px", fontSize: 10, borderColor: "#93c5fd", background: "#dbeafe", color: "#1d4ed8" }}>apply</button>
                                </div>
                              )}
                            </td>
                            <td style={{ ...td, textAlign: "right" }}>{fa.rowsIncluded}</td>
                            <td style={{ ...td, textAlign: "right", cursor: excludedCount ? "help" : "default" }} title={excludedTitle}>{excludedCount}</td>
                            <td
                              style={{ ...td, textAlign: "right", color: fa.netOk ? "var(--theme-action)" : analysis.splitExcludedRows > 0 ? "#b45309" : "#b91c1c", fontWeight: fa.netOk ? 400 : 700 }}
                              title={fa.netOk
                                ? "Nets to zero in every period"
                                : [
                                  analysis.splitExcludedRows > 0
                                    ? "Does not net to zero. Rows were dropped as N/A, which accounts for some or all of this."
                                    : "Does not net to zero — a subtotal row slipped in or a column is mis-tagged.",
                                  ...(fa.netBreaks ?? []).map((b) => `${b.entity} ${b.period.startsWith("FY:") ? `FY${b.period.slice(5)}` : keyLabel(b.period)}: ${fmtNum(b.net)}`),
                                ].join("\n")}
                            >
                              {fa.status === "error" ? "" : fa.netOk ? "0 ✓" : fmtNum(fa.net)}
                            </td>
                            <td style={{ ...td, color: fa.duplicateAccounts.length ? "#b45309" : "#9ca3af" }} title={fa.duplicateAccounts.join(", ")}>{fa.duplicateAccounts.length || ""}</td>
                            <td style={{ ...td, whiteSpace: "nowrap" }}>
                              <button onClick={() => void inspect(fa)} style={ghostBtn}>{isTemplate ? "Edit tags" : "Inspect"}</button>
                              {" "}
                              {fa.status === "manual" && <button onClick={() => void clearOverride(fa.id)} style={ghostBtn} title="Drop the manual tags and match from the template again">Re-match</button>}
                              {fa.status === "shifted" && <button onClick={() => void clearShift(fa.id)} style={ghostBtn} title="Undo the column shift and match from the template again">Undo shift</button>}
                              {" "}
                              <button onClick={() => void removeFromReview(fa.id)} style={dangerBtn} title="Remove this file">✕</button>
                            </td>
                          </tr>
                          {fa.error && (
                            <tr><td colSpan={11} style={{ ...td, color: "#b91c1c", background: "#fef2f2", whiteSpace: "normal" }}>{fa.error}</td></tr>
                          )}
                        </React.Fragment>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              {/* cross-file checks */}
              <div style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
                <CheckBox
                  title="Periods with identical figures"
                  ok={(analysis.cross.identicalColumns ?? []).length === 0}
                  okText="No two periods hold the same numbers."
                >
                  {(analysis.cross.identicalColumns ?? []).map((g, i) => (
                    <div key={i}>
                      <strong>{g.labels.join(", ")}</strong> are identical down every account — almost certainly one source read more than once. Check the Sheet column above.
                    </div>
                  ))}
                </CheckBox>
                <CheckBox title="Missing months" ok={analysis.cross.missingPeriods.length === 0} okText="Every entity has a continuous run of months.">
                  {analysis.cross.missingPeriods.map((m) => (
                    <div key={m.entity}><strong>{m.entity}</strong>: {m.periods.map(keyLabel).join(", ")}</div>
                  ))}
                </CheckBox>
                <CheckBox title="Two files on one month" ok={analysis.cross.duplicatePeriods.length === 0} okText="No month is covered by more than one file.">
                  {analysis.cross.duplicatePeriods.map((d) => (
                    <div key={`${d.entity}-${d.period}`}><strong>{d.entity}</strong> {keyLabel(d.period)}: {d.files.join(", ")} — amounts will be summed</div>
                  ))}
                </CheckBox>
                <CheckBox
                  title="Accounts not in every month"
                  ok={analysis.cross.partialCoverageTotal === 0}
                  okText="Every account appears in every month."
                  neutral
                  summary={`${analysis.cross.partialCoverageTotal} account${analysis.cross.partialCoverageTotal === 1 ? "" : "s"} — normal for accounts opened or closed mid-period; blank in the months they are absent.`}
                >
                  <div style={{ maxHeight: 140, overflowY: "auto" }}>
                    {analysis.cross.partialCoverage.map((p, i) => (
                      <div key={i}>{p.entity} · {p.account} {p.name} <span style={{ color: "#9ca3af" }}>({p.presentIn} of {p.of})</span></div>
                    ))}
                    {analysis.cross.partialCoverageTotal > analysis.cross.partialCoverage.length && (
                      <div style={{ color: "#9ca3af" }}>… and {analysis.cross.partialCoverageTotal - analysis.cross.partialCoverage.length} more</div>
                    )}
                  </div>
                </CheckBox>
              </div>
            </div>
          )}

          {/* inspect one file */}
          {inspecting && (() => {
            const f = units.find((x) => x.id === inspecting.fileId);
            if (!f) return null;
            return (
              <div style={{ ...card, border: "1px solid #93c5fd", background: "#f8fbff" }}>
                <div style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
                  <div style={{ ...h2, color: "#1d4ed8" }}>Inspect: {f.fileName} › {f.sheetName}</div>
                </div>
                <div style={hint}>These are the tags the matcher resolved for this file. Fix the header row or any column, then save. Saved tags apply to this file only.</div>
                <SheetTagger
                  preview={inspecting.preview}
                  headerRow={inspecting.headerRow}
                  tags={inspecting.tags}
                  onChange={(hr, t) => setInspecting({ ...inspecting, headerRow: hr, tags: t })}
                />
                <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                  <button onClick={() => void saveInspect()} disabled={!!busy || !!templateProblem(inspecting.tags, entityMode)} style={primaryBtn(!!busy || !!templateProblem(inspecting.tags, entityMode))}>Save tags for this file</button>
                  <button onClick={() => setInspecting(null)} style={ghostBtn}>Cancel</button>
                  {templateProblem(inspecting.tags, entityMode) && <span style={{ ...hint, color: "#b45309" }}>{templateProblem(inspecting.tags, entityMode)}</span>}
                </div>
              </div>
            );
          })()}

          <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
            <button onClick={() => { setInspecting(null); setPhase("tagging"); }} style={ghostBtn}>← Tag columns</button>
            <button onClick={() => void build()} disabled={!canBuild} style={primaryBtn(!canBuild)}>⬇ Build monthly TB</button>
            {stale && !busy && (
              <>
                <span style={{ ...hint, color: "#b45309" }}>Settings changed since these checks ran.</span>
                <button onClick={() => void runAnalyze()} style={ghostBtn}>Re-run checks</button>
              </>
            )}
            {blockers.length > 0 && <span style={{ ...hint, color: "#b91c1c" }}>Fix or remove the {blockers.length} red file{blockers.length === 1 ? "" : "s"} before building.</span>}
            {fiscalBlocked && <span style={{ ...hint, color: "#b91c1c" }}>Classify the remaining accounts as IS or BS before building fiscal-year columns.</span>}
            {downloaded && <span style={{ ...hint, color: "var(--theme-action)" }}>Downloaded. Upload it to the Databook Builder — the columns already match its defaults.</span>}
          </div>
        </>
      )}
    </section>
  );
};

const CheckBox: React.FC<{ title: string; ok: boolean; okText: string; neutral?: boolean; summary?: string; children?: React.ReactNode }> = ({ title, ok, okText, neutral, summary, children }) => {
  const [open, setOpen] = useState(false);
  const color = ok ? "var(--theme-action)" : neutral ? "#6b7280" : "#b45309";
  const bg = ok ? "#f0fdf4" : neutral ? "#f9fafb" : "#fffbeb";
  const border = ok ? "#bbf7d0" : neutral ? "#e5e7eb" : "#fde68a";
  return (
    <div style={{ flex: "1 1 240px", background: bg, border: `1px solid ${border}`, borderRadius: 6, padding: "6px 10px", fontSize: 11 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
        <span style={{ fontWeight: 700, color }}>{ok ? "✓" : neutral ? "•" : "⚠"} {title}</span>
        {!ok && summary && <button onClick={() => setOpen((o) => !o)} style={{ ...ghostBtn, padding: "0 6px", fontSize: 10 }}>{open ? "hide" : "show"}</button>}
      </div>
      {ok ? (
        <div style={{ color: "var(--theme-ink-soft)", marginTop: 2 }}>{okText}</div>
      ) : (
        <div style={{ color: "#374151", marginTop: 2 }}>
          {summary && <div>{summary}</div>}
          {(!summary || open) && children}
        </div>
      )}
    </div>
  );
};

export default TbAggregatorPanel;
